/**
 * Web-specific auth service using Firebase v5 Compat SDK
 * 
 * Web OAuth requires Firebase v5 compat because the hosted auth handler expects
 * the legacy URL format (providerId in URL params, not sessionStorage).
 * This matches www.mentorist.app's working implementation.
 * 
 * Native apps (iOS/Android) use authService.ts with modern Firebase SDK.
 */

import { ServiceResponse } from './authService';

// Track if Firebase v5 compat is loaded
let firebaseV5Ready: Promise<void> | null = null;

// Dynamically load Firebase v5 compat scripts
const loadFirebaseV5Compat = (): Promise<void> => {
  if (firebaseV5Ready) return firebaseV5Ready;

  firebaseV5Ready = new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).firebase?.auth) {
      resolve();
      return;
    }

    // Load firebase-app.js
    const appScript = document.createElement('script');
    appScript.src = 'https://www.gstatic.com/firebasejs/5.10.1/firebase-app.js';
    appScript.onload = () => {
      // Load firebase-auth.js
      const authScript = document.createElement('script');
      authScript.src = 'https://www.gstatic.com/firebasejs/5.10.1/firebase-auth.js';
      authScript.onload = async () => {
        // Initialize Firebase v5 with Same-Origin authDomain for production
        const firebaseV5 = (window as any).firebase;
        if (!firebaseV5.apps.length) {
          firebaseV5.initializeApp({
            apiKey: 'AIzaSyAIIkNqT_xn95ZoHAsLDb0eli8b4Vz5cbw',
            authDomain: 'ai.mentorist.app',
            databaseURL: 'https://habitcoach-9c912.firebaseio.com',
            projectId: 'habitcoach-9c912',
            storageBucket: 'habitcoach-9c912.appspot.com',
            messagingSenderId: '349271191157',
          });
        }
        
        // Set auth persistence to LOCAL so users stay logged in across page refreshes
        try {
          await firebaseV5.auth().setPersistence(firebaseV5.auth.Auth.Persistence.LOCAL);
          console.log('✅ Firebase v5 Auth persistence set to LOCAL');
        } catch (error) {
          console.error('⚠️ Failed to set v5 auth persistence:', error);
        }
        
        resolve();
      };
      authScript.onerror = () => reject(new Error('Failed to load firebase-auth.js'));
      document.head.appendChild(authScript);
    };
    appScript.onerror = () => reject(new Error('Failed to load firebase-app.js'));
    document.head.appendChild(appScript);
  });

  return firebaseV5Ready;
};

export const signInWithApple = async (): Promise<ServiceResponse<any>> => {
  try {
    await loadFirebaseV5Compat();
    
    const firebaseV5 = (window as any).firebase;
    const provider = new firebaseV5.auth.OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    const result = await firebaseV5.auth().signInWithPopup(provider);
    
    // Return immediately - authShared.ts will wait for v12 sync via onAuthStateChanged
    return { 
      success: true, 
      data: { 
        user: result.user,
        credential: result.credential,
      } 
    };
  } catch (error: any) {
    // User cancelled
    if (error?.code === 'auth/popup-closed-by-user' || 
        error?.code === 'auth/cancelled-popup-request') {
      return { success: false, error: 'User canceled Apple Sign-In.' };
    }

    // Popup blocked by browser
    const errorMessage = error?.message || '';
    if (errorMessage.includes('popup') && 
        (errorMessage.includes('blocked') || errorMessage.includes('connection'))) {
      return {
        success: false,
        error: 'POPUP_BLOCKED',
        data: {
          userMessage: 'Pop-ups are blocked in your browser. To sign in with Apple, please:\n\n1. Enable pop-ups for this site in your browser settings\n2. Tap the sign-in button again\n\nWe need pop-ups to securely connect with Apple for authentication.',
        },
      };
    }

    console.error('signInWithApple (v5 compat) error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to sign in with Apple.',
    };
  }
};

export const signInWithGoogle = async (): Promise<ServiceResponse<any>> => {
  try {
    await loadFirebaseV5Compat();
    
    const firebaseV5 = (window as any).firebase;
    const provider = new firebaseV5.auth.GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    const result = await firebaseV5.auth().signInWithPopup(provider);
    
    // Return immediately - authShared.ts will wait for v12 sync via onAuthStateChanged
    return { 
      success: true, 
      data: { 
        user: result.user,
        credential: result.credential,
      } 
    };
  } catch (error: any) {
    // User cancelled
    if (error?.code === 'auth/popup-closed-by-user' || 
        error?.code === 'auth/cancelled-popup-request') {
      return { success: false, error: 'User canceled Google Sign-In.' };
    }

    // Popup blocked by browser
    const errorMessage = error?.message || '';
    if (errorMessage.includes('popup') && 
        (errorMessage.includes('blocked') || errorMessage.includes('connection'))) {
      return {
        success: false,
        error: 'POPUP_BLOCKED',
        data: {
          userMessage: 'Pop-ups are blocked in your browser. To sign in with Google, please:\n\n1. Enable pop-ups for this site in your browser settings\n2. Tap the sign-in button again\n\nWe need pop-ups to securely connect with Google for authentication.',
        },
      };
    }

    console.error('signInWithGoogle (v5 compat) error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to sign in with Google.',
    };
  }
};

